import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, addDays, startOfWeek, addHours, isSameDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { cn } from '../lib/utils';
import { 
  ChevronLeft, 
  ChevronRight, 
  CalendarIcon,
  Clock,
  Users,
  Phone,
  Mail,
  MapPin,
  LayoutGrid,
  List,
  Calendar as CalendarViewIcon
} from 'lucide-react';

// Time slots from 10:00 to 24:00
const HOURS = Array.from({ length: 15 }, (_, i) => i + 10);
const HOUR_HEIGHT = 60; // pixels per hour

const statusColors = {
  pending: 'bg-yellow-500',
  confirmed: 'bg-blue-500',
  seated: 'bg-green-500',
  completed: 'bg-gray-500',
  cancelled: 'bg-red-500',
  no_show: 'bg-red-700'
};

const statusLabels = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  seated: 'Sentado',
  completed: 'Completada',
  cancelled: 'Cancelada',
  no_show: 'No Show'
};

export default function ReservationCalendar({ 
  reservations = [], 
  tables = [], 
  selectedDate,
  onDateChange,
  onReservationClick,
  onCreateReservation
}) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week', 'table'
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (selectedDate) {
      setCurrentDate(selectedDate);
    }
  }, [selectedDate]);

  // Scroll to current time on mount
  useEffect(() => {
    if (scrollRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      if (currentHour >= 10 && currentHour <= 24) {
        const scrollPosition = (currentHour - 10) * HOUR_HEIGHT;
        scrollRef.current.scrollTop = scrollPosition - 100;
      }
    }
  }, [viewMode]);

  // Filter reservations for current view
  const filteredReservations = useMemo(() => {
    return reservations.filter(r => {
      if (!r.date) return false;
      const resDate = typeof r.date === 'string' ? parseISO(r.date) : r.date;
      
      if (viewMode === 'day') {
        return isSameDay(resDate, currentDate);
      } else if (viewMode === 'week') {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);
        return resDate >= weekStart && resDate <= weekEnd;
      }
      return true;
    });
  }, [reservations, currentDate, viewMode]);

  // Get week days for week view
  const weekDays = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const navigateDate = (direction) => {
    const newDate = viewMode === 'week' 
      ? addDays(currentDate, direction * 7)
      : addDays(currentDate, direction);
    setCurrentDate(newDate);
    onDateChange?.(newDate);
  };

  const getReservationPosition = (reservation) => {
    if (!reservation.time) return null;
    const [hours, minutes] = reservation.time.split(':').map(Number);
    const top = (hours - 10) * HOUR_HEIGHT + (minutes / 60) * HOUR_HEIGHT;
    const height = (reservation.duration || 90) / 60 * HOUR_HEIGHT;
    return { top, height };
  };

  const getTableColumn = (reservation) => {
    if (!reservation.table_ids || reservation.table_ids.length === 0) return 0;
    const tableIndex = tables.findIndex(t => t.id === reservation.table_ids[0]);
    return tableIndex >= 0 ? tableIndex : 0;
  };

  const handleReservationClick = (reservation) => {
    setSelectedReservation(reservation);
    onReservationClick?.(reservation);
  };

  // Day View
  const DayView = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <div className="relative min-h-[900px]">
          {/* Time grid */}
          <div className="absolute left-0 top-0 w-16 h-full border-r border-border">
            {HOURS.map(hour => (
              <div 
                key={hour} 
                className="h-[60px] flex items-start justify-end pr-2 text-xs text-muted-foreground"
              >
                {hour}:00
              </div>
            ))}
          </div>

          {/* Reservations area */}
          <div className="ml-16 relative">
            {/* Hour lines */}
            {HOURS.map(hour => (
              <div 
                key={hour} 
                className="h-[60px] border-b border-border/50"
              />
            ))}

            {/* Current time indicator */}
            {isSameDay(currentDate, new Date()) && (
              <div 
                className="absolute left-0 right-0 border-t-2 border-red-500 z-10"
                style={{ 
                  top: `${(new Date().getHours() - 10) * HOUR_HEIGHT + (new Date().getMinutes() / 60) * HOUR_HEIGHT}px` 
                }}
              >
                <div className="w-3 h-3 bg-red-500 rounded-full -mt-1.5 -ml-1.5" />
              </div>
            )}

            {/* Reservations */}
            <div className="absolute inset-0">
              {filteredReservations.map((reservation, index) => {
                const position = getReservationPosition(reservation);
                if (!position) return null;
                
                // Distribute overlapping reservations
                const overlapping = filteredReservations.filter(r => {
                  const pos = getReservationPosition(r);
                  return pos && Math.abs(pos.top - position.top) < 30;
                });
                const overlapIndex = overlapping.findIndex(r => r.id === reservation.id);
                const width = 100 / Math.max(overlapping.length, 1);
                const left = width * overlapIndex;

                return (
                  <div
                    key={reservation.id}
                    className={cn(
                      "absolute rounded-lg p-2 cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg overflow-hidden",
                      statusColors[reservation.status] || 'bg-primary'
                    )}
                    style={{
                      top: `${position.top}px`,
                      height: `${Math.max(position.height, 40)}px`,
                      left: `${left}%`,
                      width: `calc(${width}% - 8px)`,
                      marginLeft: '4px'
                    }}
                    onClick={() => handleReservationClick(reservation)}
                    data-testid={`reservation-block-${reservation.id}`}
                  >
                    <div className="text-white text-sm font-medium truncate">
                      {reservation.guest_name}
                    </div>
                    <div className="text-white/80 text-xs flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {reservation.time}
                      <Users className="h-3 w-3 ml-2" />
                      {reservation.party_size}
                    </div>
                    {reservation.table_ids?.length > 0 && (
                      <div className="text-white/70 text-xs mt-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        Mesa {tables.find(t => t.id === reservation.table_ids[0])?.name || reservation.table_ids[0]}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Week View
  const WeekView = () => (
    <div className="flex flex-col h-full">
      {/* Week header */}
      <div className="flex border-b border-border">
        <div className="w-16 shrink-0" />
        {weekDays.map(day => (
          <div 
            key={day.toString()} 
            className={cn(
              "flex-1 p-2 text-center border-l border-border",
              isSameDay(day, new Date()) && "bg-primary/10"
            )}
          >
            <div className="text-xs text-muted-foreground">
              {format(day, 'EEE', { locale: es })}
            </div>
            <div className={cn(
              "text-lg font-semibold",
              isSameDay(day, new Date()) && "text-primary"
            )}>
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {/* Week grid */}
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <div className="flex min-h-[900px]">
          {/* Time column */}
          <div className="w-16 shrink-0 border-r border-border">
            {HOURS.map(hour => (
              <div 
                key={hour} 
                className="h-[60px] flex items-start justify-end pr-2 text-xs text-muted-foreground"
              >
                {hour}:00
              </div>
            ))}
          </div>

          {/* Days columns */}
          {weekDays.map(day => (
            <div 
              key={day.toString()} 
              className="flex-1 relative border-l border-border"
            >
              {/* Hour lines */}
              {HOURS.map(hour => (
                <div 
                  key={hour} 
                  className="h-[60px] border-b border-border/30"
                />
              ))}

              {/* Reservations for this day */}
              {filteredReservations
                .filter(r => {
                  const resDate = typeof r.date === 'string' ? parseISO(r.date) : r.date;
                  return isSameDay(resDate, day);
                })
                .map(reservation => {
                  const position = getReservationPosition(reservation);
                  if (!position) return null;

                  return (
                    <div
                      key={reservation.id}
                      className={cn(
                        "absolute left-1 right-1 rounded p-1 cursor-pointer text-white text-xs overflow-hidden",
                        statusColors[reservation.status] || 'bg-primary'
                      )}
                      style={{
                        top: `${position.top}px`,
                        height: `${Math.max(position.height, 30)}px`,
                      }}
                      onClick={() => handleReservationClick(reservation)}
                    >
                      <div className="font-medium truncate">{reservation.guest_name}</div>
                      <div className="opacity-80">{reservation.time}</div>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Table View (reservations grouped by table)
  const TableView = () => (
    <div className="flex flex-col h-full">
      {/* Tables header */}
      <div className="flex border-b border-border">
        <div className="w-16 shrink-0" />
        {tables.map(table => (
          <div 
            key={table.id} 
            className="flex-1 p-2 text-center border-l border-border min-w-[120px]"
          >
            <div className="font-medium">{table.name}</div>
            <div className="text-xs text-muted-foreground">Cap: {table.capacity}</div>
          </div>
        ))}
        <div className="flex-1 p-2 text-center border-l border-border min-w-[120px]">
          <div className="font-medium">Sin Mesa</div>
        </div>
      </div>

      {/* Table grid */}
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <div className="flex min-h-[900px]">
          {/* Time column */}
          <div className="w-16 shrink-0 border-r border-border">
            {HOURS.map(hour => (
              <div 
                key={hour} 
                className="h-[60px] flex items-start justify-end pr-2 text-xs text-muted-foreground"
              >
                {hour}:00
              </div>
            ))}
          </div>

          {/* Table columns */}
          {[...tables, { id: 'none', name: 'Sin Mesa' }].map((table, tableIndex) => (
            <div 
              key={table.id} 
              className="flex-1 relative border-l border-border min-w-[120px]"
            >
              {/* Hour lines */}
              {HOURS.map(hour => (
                <div 
                  key={hour} 
                  className="h-[60px] border-b border-border/30"
                />
              ))}

              {/* Reservations for this table */}
              {filteredReservations
                .filter(r => {
                  if (table.id === 'none') {
                    return !r.table_ids || r.table_ids.length === 0;
                  }
                  return r.table_ids?.includes(table.id);
                })
                .map(reservation => {
                  const position = getReservationPosition(reservation);
                  if (!position) return null;

                  return (
                    <div
                      key={reservation.id}
                      className={cn(
                        "absolute left-1 right-1 rounded p-1 cursor-pointer text-white text-xs overflow-hidden",
                        statusColors[reservation.status] || 'bg-primary'
                      )}
                      style={{
                        top: `${position.top}px`,
                        height: `${Math.max(position.height, 30)}px`,
                      }}
                      onClick={() => handleReservationClick(reservation)}
                    >
                      <div className="font-medium truncate">{reservation.guest_name}</div>
                      <div className="opacity-80 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {reservation.time}
                        <Users className="h-3 w-3 ml-1" />
                        {reservation.party_size}
                      </div>
                    </div>
                  );
                })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col" data-testid="reservation-calendar">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        {/* Date navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[200px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {viewMode === 'week' 
                  ? `${format(weekDays[0], 'd MMM', { locale: es })} - ${format(weekDays[6], 'd MMM yyyy', { locale: es })}`
                  : format(currentDate, 'EEEE, d MMMM yyyy', { locale: es })
                }
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={currentDate}
                onSelect={(date) => {
                  if (date) {
                    setCurrentDate(date);
                    onDateChange?.(date);
                    setShowDatePicker(false);
                  }
                }}
                locale={es}
              />
            </PopoverContent>
          </Popover>

          <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>

          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setCurrentDate(new Date());
              onDateChange?.(new Date());
            }}
          >
            Hoy
          </Button>
        </div>

        {/* View mode selector */}
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'day' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('day')}
              data-testid="view-mode-day"
            >
              <CalendarViewIcon className="h-4 w-4 mr-1" />
              Día
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('week')}
              data-testid="view-mode-week"
            >
              <List className="h-4 w-4 mr-1" />
              Semana
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              data-testid="view-mode-table"
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              Por Mesa
            </Button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-border bg-muted/30">
        <span className="text-xs text-muted-foreground">Estado:</span>
        {Object.entries(statusLabels).map(([status, label]) => (
          <div key={status} className="flex items-center gap-1">
            <div className={cn("w-3 h-3 rounded", statusColors[status])} />
            <span className="text-xs">{label}</span>
          </div>
        ))}
      </div>

      {/* Calendar content */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'day' && <DayView />}
        {viewMode === 'week' && <WeekView />}
        {viewMode === 'table' && <TableView />}
      </div>

      {/* Reservation Detail Dialog */}
      <Dialog open={!!selectedReservation} onOpenChange={() => setSelectedReservation(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalles de Reservación</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{selectedReservation.guest_name}</h3>
                <Badge className={cn("text-white", statusColors[selectedReservation.status])}>
                  {statusLabels[selectedReservation.status]}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                  <span>{format(parseISO(selectedReservation.date), 'dd/MM/yyyy')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedReservation.time} ({selectedReservation.duration} min)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedReservation.party_size} personas</span>
                </div>
                {selectedReservation.table_ids?.length > 0 && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>Mesa {tables.find(t => t.id === selectedReservation.table_ids[0])?.name}</span>
                  </div>
                )}
              </div>

              {selectedReservation.guest_phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedReservation.guest_phone}</span>
                </div>
              )}
              
              {selectedReservation.guest_email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{selectedReservation.guest_email}</span>
                </div>
              )}

              {selectedReservation.notes && (
                <div className="p-3 bg-muted rounded-lg text-sm">
                  <div className="text-xs text-muted-foreground mb-1">Notas:</div>
                  {selectedReservation.notes}
                </div>
              )}

              {selectedReservation.special_requests && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                  <div className="text-xs text-amber-600 mb-1">Solicitudes especiales:</div>
                  {selectedReservation.special_requests}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button 
                  className="flex-1" 
                  onClick={() => {
                    onReservationClick?.(selectedReservation);
                    setSelectedReservation(null);
                  }}
                >
                  Editar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedReservation(null)}
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
